import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { showError } from '../utils/toast';

const ExamResult = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState(null);
  const [examMeta, setExamMeta] = useState(null);
  const [resultsAllowed, setResultsAllowed] = useState(true);
  const [allAttempts, setAllAttempts] = useState([]);
  const [expandedAttempts, setExpandedAttempts] = useState(new Set([submissionId]));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const fetchResult = async () => {
    try {
      const submissionDoc = await getDoc(doc(db, 'examSubmissions', submissionId));
      
      if (!submissionDoc.exists()) {
        showError('Submission not found');
        navigate('/student-dashboard');
        return;
      }

      const submissionData = { id: submissionDoc.id, ...submissionDoc.data() };
      
      // Verify the student owns this submission
      const studentId = localStorage.getItem('studentId');
      if (submissionData.studentId !== studentId) {
        showError('Unauthorized access');
        navigate('/student-dashboard');
        return;
      }

      setSubmission(submissionData);

      // Gate result visibility based on exam configuration
      const examDoc = await getDoc(doc(db, 'exams', submissionData.examId));
      if (examDoc.exists()) {
        const examData = { id: examDoc.id, ...examDoc.data() };
        setExamMeta(examData);

        const endDateTime = new Date(`${examData.endDate}T${examData.endTime}`);
        const now = new Date();
        const canView = !!examData.showResultImmediately || now > endDateTime;
        setResultsAllowed(canView);

        if (!canView) {
          showError('Results are not published yet. Please check back later.');
          navigate('/student-dashboard', { replace: true });
          return;
        }
      }

      // Fetch all attempts for this exam by this student
      const attemptsQuery = query(
        collection(db, 'examSubmissions'),
        where('examId', '==', submissionData.examId),
        where('studentId', '==', studentId),
        where('status', '==', 'completed'),
        orderBy('submittedAt', 'desc')
      );
      
      const attemptsSnapshot = await getDocs(attemptsQuery);
      const attemptsList = [];
      attemptsSnapshot.forEach((doc) => {
        attemptsList.push({ id: doc.id, ...doc.data() });
      });
      
      setAllAttempts(attemptsList);

    } catch (error) {
      console.error('Error fetching result:', error);
      showError('Failed to load results');
      navigate('/student-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return { grade: 'A+', color: 'text-green-600', bg: 'bg-green-100' };
    if (percentage >= 80) return { grade: 'A', color: 'text-green-600', bg: 'bg-green-100' };
    if (percentage >= 70) return { grade: 'B+', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (percentage >= 60) return { grade: 'B', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (percentage >= 50) return { grade: 'C', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (percentage >= 40) return { grade: 'D', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { grade: 'F', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const toggleAttempt = (attemptId) => {
    setExpandedAttempts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attemptId)) {
        newSet.delete(attemptId);
      } else {
        newSet.add(attemptId);
      }
      return newSet;
    });
  };

  const renderAttemptResults = (attemptData, attemptNumber, isExpanded) => {
    const percentage = attemptData.percentage || 0;
    const gradeInfo = getGrade(percentage);
    const questionResults = attemptData.questionResults || {};
    const questionsArray = Object.values(questionResults);
    
    // Debug: Log question results for verification
    console.log('Rendering attempt:', attemptNumber);
    console.log('Question Results:', questionsArray);
    questionsArray.forEach((q, idx) => {
      console.log(`Q${idx + 1}:`, {
        wasAnswered: q.wasAnswered,
        isCorrect: q.isCorrect,
        studentAnswer: q.studentAnswer
      });
    });
    
    const correctAnswers = questionsArray.filter(q => q.isCorrect === true).length;
    const wrongAnswers = questionsArray.filter(q => q.wasAnswered === true && q.isCorrect === false).length;
    const unanswered = questionsArray.filter(q => q.wasAnswered === false || q.wasAnswered === undefined).length;
    
    console.log('Calculated Stats:', { correctAnswers, wrongAnswers, unanswered, total: questionsArray.length });

    // Format score to avoid floating point precision issues
    const formattedScore = parseFloat(attemptData.totalScore || 0).toFixed(2);
    const formattedTotalMarks = parseFloat(attemptData.totalMarks || 0).toFixed(2);
    const formattedPercentage = parseFloat(percentage).toFixed(1);

    return (
      <div key={attemptData.id} className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
        {/* Attempt Header - Clickable */}
        <div 
          onClick={() => toggleAttempt(attemptData.id)}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 cursor-pointer hover:from-indigo-700 hover:to-purple-700 transition"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-bold text-white">
                Attempt {attemptNumber} {attemptData.id === submissionId && '(Current)'}
              </h2>
              <span className="bg-white bg-opacity-20 text-white px-4 py-1 rounded-full text-sm font-semibold">
                {formattedPercentage}% - {gradeInfo.grade}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-indigo-100 text-sm">
                {new Date(attemptData.submittedAt).toLocaleString()}
              </span>
              <svg 
                className={`h-6 w-6 text-white transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Collapsible Content */}
        {isExpanded && (
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Score Card */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200">
                <div className="text-center">
                  <p className="text-gray-600 mb-2">Score</p>
                  <div className="text-5xl font-bold text-indigo-600 mb-2">
                    {formattedScore}/{formattedTotalMarks}
                  </div>
                  <div className="text-2xl font-bold text-purple-600">
                    {formattedPercentage}%
                  </div>
                </div>
              </div>

              {/* Grade Card */}
              <div className={`${gradeInfo.bg} rounded-xl p-6 border-2 border-current ${gradeInfo.color}`}>
                <div className="text-center">
                  <p className="text-gray-600 mb-2">Grade</p>
                  <div className={`text-6xl font-bold ${gradeInfo.color}`}>
                    {gradeInfo.grade}
                  </div>
                  <p className="mt-2 text-sm font-semibold">
                    {percentage >= 50 ? 'Passed' : 'Failed'}
                  </p>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{questionsArray.length}</div>
                <div className="text-sm text-gray-600">Total Questions</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{correctAnswers}</div>
                <div className="text-sm text-gray-600">Correct</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{wrongAnswers}</div>
                <div className="text-sm text-gray-600">Wrong</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{unanswered}</div>
                <div className="text-sm text-gray-600">Unanswered</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{attemptData.tabSwitchCount || 0}</div>
                <div className="text-sm text-gray-600">Tab Switches</div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid md:grid-cols-2 gap-4 pt-6 border-t border-gray-200 mb-8">
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">Submitted At</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(attemptData.submittedAt).toLocaleString()}
                  </p>
                </div>
              </div>
              {attemptData.autoSubmitted && (
                <div className="flex items-center gap-3">
                  <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-orange-600 font-semibold">Auto-submitted</p>
                    <p className="text-xs text-gray-500">Time limit reached</p>
                  </div>
                </div>
              )}
            </div>

            {/* Question-wise Results */}
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Detailed Results</h3>
              
              <div className="space-y-6">
                {questionsArray.map((question, index) => (
                  <div 
                    key={index}
                    className={`border-2 rounded-xl p-6 ${
                      question.isCorrect 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-white px-3 py-1 rounded-lg font-bold text-gray-700">
                            Q{index + 1}
                          </span>
                          <span className={`px-3 py-1 rounded-lg font-semibold ${
                            question.isCorrect 
                              ? 'bg-green-600 text-white' 
                              : 'bg-red-600 text-white'
                          }`}>
                            {question.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                          </span>
                          <span className={`text-sm font-semibold ${
                            question.scored < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {question.scored > 0 ? '+' : ''}{parseFloat(question.scored || 0).toFixed(2)}/{parseFloat(question.marks || 0).toFixed(2)} marks
                          </span>
                        </div>
                        <p className="text-gray-900 font-medium">{question.questionText}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Your Answer:</p>
                        {question.studentAnswerImage ? (
                          <div>
                            <p className={`font-semibold mb-2 ${
                              question.isCorrect ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {question.studentAnswer}
                            </p>
                            <img 
                              src={question.studentAnswerImage} 
                              alt="Your Answer" 
                              className="max-w-xs rounded-lg shadow-md border-2 border-gray-300"
                            />
                          </div>
                        ) : (
                          <p className={`font-semibold ${
                            question.isCorrect ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {question.studentAnswer}
                          </p>
                        )}
                      </div>

                      {!question.isCorrect && question.correctAnswer && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Correct Answer:</p>
                          {question.correctAnswerImage ? (
                            <div>
                              <p className="font-semibold text-green-700 mb-2">
                                {question.correctAnswer}
                              </p>
                              <img 
                                src={question.correctAnswerImage} 
                                alt="Correct Answer" 
                                className="max-w-xs rounded-lg shadow-md border-2 border-green-300"
                              />
                            </div>
                          ) : (
                            <p className="font-semibold text-green-700">
                              {question.correctAnswer}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="text-gray-600 mt-4 font-medium">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!submission) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Exam Results</h1>
          <p className="text-xl text-gray-600">{submission.examName}</p>
          {allAttempts.length > 1 && (
            <p className="text-sm text-gray-500 mt-2">
              Total Attempts: {allAttempts.length} | Click on any attempt to expand/collapse
            </p>
          )}
        </div>

        {/* All Attempts - Most Recent First */}
        {allAttempts.map((attempt, index) => 
          renderAttemptResults(
            attempt, 
            allAttempts.length - index, 
            expandedAttempts.has(attempt.id)
          )
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center mt-8">
          <button
            onClick={() => navigate('/student-dashboard')}
            className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
          >
            Back to Dashboard
          </button>
          <button
            onClick={() => window.print()}
            className="px-8 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition"
          >
            Print Results
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExamResult;
